import { z } from "zod";

/**
 * OTP Target Configuration
 * Represents the configuration for a specific OTP target (e.g., user account, service)
 */
export interface OTPTargetConfig {
  /** Unique identifier for the OTP target (alphanumeric + hyphens, max 100 chars) */
  targetId: string;
  /** Human-readable name for the target */
  name: string;
  /** HMAC algorithm used for OTP generation (e.g., "SHA1", "SHA256", "SHA512") */
  algorithm: "SHA1" | "SHA256" | "SHA512";
  /** Number of digits in the generated OTP code (typically 6 or 8) */
  digits: number;
  /** Time period in seconds for TOTP (typically 30) */
  period: number;
  /** Timestamp when the target was created */
  createdAt: Date;
  /** Timestamp when the target was last updated */
  updatedAt: Date;
}

/**
 * OTP Secret
 * Extends OTPTargetConfig with a reference to the vault-stored secret
 * The actual secret value is never included in this type (stored securely in vault)
 */
export interface OTPSecret extends OTPTargetConfig {
  /** Reference ID to the secret stored in vault (e.g., AWS Secrets Manager, HashiCorp Vault) */
  secretId: string;
}

/**
 * Zod schema for validating OTP Target Configuration
 * Transforms serialized timestamp strings to Date objects
 */
export const OTPTargetValidation = z.object({
  targetId: z
    .string()
    .regex(
      /^[a-zA-Z0-9-]+$/,
      "targetId must contain only alphanumeric characters and hyphens",
    )
    .max(100, "targetId must not exceed 100 characters"),
  name: z
    .string()
    .min(1, "name is required")
    .max(255, "name must not exceed 255 characters"),
  algorithm: z.enum(["SHA1", "SHA256", "SHA512"]),
  digits: z
    .number()
    .int()
    .min(4, "digits must be at least 4")
    .max(10, "digits must not exceed 10"),
  period: z.number().int().positive("period must be positive"),
  createdAt: z.union([z.date(), z.string().transform((s) => new Date(s))]),
  updatedAt: z.union([z.date(), z.string().transform((s) => new Date(s))]),
});

/**
 * Zod schema for OTP Secret (includes secretId)
 */
export const OTPSecretValidation = OTPTargetValidation.extend({
  secretId: z.string().min(1, "secretId is required"),
});

/**
 * Type inferred from OTPTargetValidation schema
 */
export type OTPTargetConfigDTO = z.infer<typeof OTPTargetValidation>;

/**
 * Type inferred from OTPSecretValidation schema
 */
export type OTPSecretDTO = z.infer<typeof OTPSecretValidation>;
